import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Target, Users, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const THEMES = [
  'Artificial Intelligence',
  'Blockchain',
  'AgriTech',
  'HealthTech',
  'FinTech',
  'EdTech',
  'Open Innovation'
];

const DIFFICULTIES = ['easy', 'medium', 'hard'];

const AdminProblemStatementsSection = ({ token }) => {
  const [problemStatements, setProblemStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedTheme, setExpandedTheme] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    theme: 'Artificial Intelligence',
    difficulty: 'medium',
    expected_outcomes: [],
    resources: []
  });
  const [newOutcome, setNewOutcome] = useState('');
  const [newResource, setNewResource] = useState('');

  useEffect(() => {
    fetchProblemStatements();
  }, []);

  const fetchProblemStatements = async () => {
    try {
      const response = await axios.get(`${API}/admin/problem-statements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProblemStatements(response.data.problem_statements || []);
    } catch (err) {
      toast.error('Failed to fetch problem statements');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/problem-statements/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Problem statement updated');
      } else {
        await axios.post(`${API}/admin/problem-statements`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Problem statement created');
      }
      fetchProblemStatements();
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save problem statement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this problem statement?')) return;
    try {
      await axios.delete(`${API}/admin/problem-statements/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Problem statement deleted');
      fetchProblemStatements();
    } catch (err) {
      toast.error('Failed to delete problem statement');
    }
  };

  const handleEdit = (ps) => {
    setFormData({
      title: ps.title,
      description: ps.description,
      theme: ps.theme,
      difficulty: ps.difficulty || 'medium',
      expected_outcomes: ps.expected_outcomes || [],
      resources: ps.resources || []
    });
    setEditingId(ps.id);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      theme: 'Artificial Intelligence',
      difficulty: 'medium',
      expected_outcomes: [],
      resources: []
    });
    setEditingId(null);
    setShowForm(false);
  };

  const addOutcome = () => {
    if (newOutcome.trim()) {
      setFormData(prev => ({
        ...prev,
        expected_outcomes: [...prev.expected_outcomes, newOutcome.trim()]
      }));
      setNewOutcome('');
    }
  };

  const removeOutcome = (index) => {
    setFormData(prev => ({
      ...prev,
      expected_outcomes: prev.expected_outcomes.filter((_, i) => i !== index)
    }));
  };

  const addResource = () => {
    if (newResource.trim()) {
      setFormData(prev => ({
        ...prev,
        resources: [...prev.resources, newResource.trim()]
      }));
      setNewResource('');
    }
  };

  const removeResource = (index) => {
    setFormData(prev => ({
      ...prev,
      resources: prev.resources.filter((_, i) => i !== index)
    }));
  };

  // Group by theme
  const groupedByTheme = problemStatements.reduce((acc, ps) => {
    if (!acc[ps.theme]) acc[ps.theme] = [];
    acc[ps.theme].push(ps);
    return acc;
  }, {});

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin h-8 w-8 border-4 border-purple-500 border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="problem-statements-section">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Problem Statement Bank</h2>
          <p className="text-sm text-gray-500">Manage problem statements for each theme</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          data-testid="add-problem-statement-btn"
        >
          <Plus size={18} />
          Add Problem Statement
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-purple-600">{problemStatements.length}</div>
          <div className="text-sm text-gray-600">Total Statements</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">
            {problemStatements.filter(p => p.is_active).length}
          </div>
          <div className="text-sm text-gray-600">Active</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-600">{Object.keys(groupedByTheme).length}</div>
          <div className="text-sm text-gray-600">Themes</div>
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-orange-600">
            {problemStatements.reduce((acc, p) => acc + (p.assigned_teams || 0), 0)}
          </div>
          <div className="text-sm text-gray-600">Assigned Teams</div>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">
                {editingId ? 'Edit Problem Statement' : 'New Problem Statement'}
              </h3>
              <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                  data-testid="ps-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 h-32"
                  required
                  data-testid="ps-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Theme *</label>
                  <select
                    value={formData.theme}
                    onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    data-testid="ps-theme-select"
                  >
                    {THEMES.map(theme => (
                      <option key={theme} value={theme}>{theme}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Difficulty</label>
                  <select
                    value={formData.difficulty}
                    onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    data-testid="ps-difficulty-select"
                  >
                    {DIFFICULTIES.map(d => (
                      <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Expected Outcomes</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newOutcome}
                    onChange={(e) => setNewOutcome(e.target.value)}
                    className="flex-1 border rounded-lg px-3 py-2"
                    placeholder="Add expected outcome"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addOutcome())}
                  />
                  <button type="button" onClick={addOutcome} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                    <Plus size={18} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.expected_outcomes.map((outcome, i) => (
                    <span key={i} className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                      {outcome}
                      <button type="button" onClick={() => removeOutcome(i)} className="hover:text-purple-900">
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Resources / Links</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newResource}
                    onChange={(e) => setNewResource(e.target.value)}
                    className="flex-1 border rounded-lg px-3 py-2"
                    placeholder="Add resource URL"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addResource())}
                  />
                  <button type="button" onClick={addResource} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                    <Plus size={18} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.resources.map((resource, i) => (
                    <span key={i} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                      {resource.length > 30 ? resource.substring(0, 30) + '...' : resource}
                      <button type="button" onClick={() => removeResource(i)} className="hover:text-blue-900">
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={resetForm} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2">
                  <Save size={18} />
                  {editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Problem Statements by Theme */}
      <div className="space-y-4">
        {THEMES.map(theme => {
          const statements = groupedByTheme[theme] || [];
          const isExpanded = expandedTheme === theme;
          
          return (
            <div key={theme} className="bg-white rounded-lg border overflow-hidden">
              <button
                onClick={() => setExpandedTheme(isExpanded ? null : theme)}
                className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <Target className="text-purple-600" size={20} />
                  <span className="font-medium">{theme}</span>
                  <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-sm">
                    {statements.length}
                  </span>
                </div>
                {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              
              {isExpanded && (
                <div className="border-t">
                  {statements.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      No problem statements for this theme
                    </div>
                  ) : (
                    <div className="divide-y">
                      {statements.map(ps => (
                        <div key={ps.id} className="p-4 hover:bg-gray-50">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900">{ps.title}</h4>
                              <p className="text-sm text-gray-600 mt-1 line-clamp-2">{ps.description}</p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                <span className={`px-2 py-0.5 rounded ${
                                  ps.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                                  ps.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-green-100 text-green-700'
                                }`}>
                                  {ps.difficulty}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Users size={12} />
                                  {ps.assigned_teams || 0} teams
                                </span>
                              </div>
                            </div>
                            <div className="flex gap-2 ml-4">
                              <button
                                onClick={() => handleEdit(ps)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                data-testid={`edit-ps-${ps.id}`}
                              >
                                <Edit2 size={16} />
                              </button>
                              <button
                                onClick={() => handleDelete(ps.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                data-testid={`delete-ps-${ps.id}`}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminProblemStatementsSection;
